import dynamic from "next/dynamic";
import Image from "next/image";
import { useRouter } from "next/router";

import { useContext, useEffect, useMemo } from "react";

import { twMerge } from "tailwind-merge";
import { useToggle } from "usehooks-ts";

import { faBookmark } from "@fortawesome/free-regular-svg-icons/faBookmark";
import { faCommentDots } from "@fortawesome/free-regular-svg-icons/faCommentDots";
import { faThumbsDown } from "@fortawesome/free-regular-svg-icons/faThumbsDown";
import { faThumbsUp } from "@fortawesome/free-regular-svg-icons/faThumbsUp";
import { faBookmark as faBookmarkSolid } from "@fortawesome/free-solid-svg-icons/faBookmark";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons/faEllipsisV";
import { faThumbsDown as faThumbsDownSolid } from "@fortawesome/free-solid-svg-icons/faThumbsDown";
import { faThumbsUp as faThumbsUpSolid } from "@fortawesome/free-solid-svg-icons/faThumbsUp";

import Button from "components/Button";
import CircleButton from "components/CircleButton";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import PrimerDownload from "components/InsightPage/PrimerDownload";
import RemovePaywallButton from "components/RemovePaywallButton";
import RepublishButton from "components/RepublishButton";
import ShareButton from "components/ShareButton";
import { TopNavBarContext } from "components/TopNavBar/TopNavBarContext";
import useActivityTracker from "hooks/useActivityTracker";
import useAuthentication from "hooks/useAuthentication";
import { useInsightInteractions } from "hooks/useInsightInteractions";

import styles from "./styles.module.scss";

const EntityDataModal = dynamic(() => import("components/EntityDataModal"));

const InsightCTAs = ({
  insight,
  mutate,
  isMobile,
}: {
  insight: any;
  mutate: any;
  isMobile?: boolean;
}) => {
  const router = useRouter();
  const { authenticated, account: loggedInAccount } = useAuthentication();
  const { trackActivity } = useActivityTracker();

  const { setRightActionButtons } = useContext(TopNavBarContext);

  const [showEntityData, toggleEntityData] = useToggle();

  const {
    showLike,
    canLike,
    canDislike,
    isLiked,
    isDisliked,
    like,
    dislike,
    isSaved,
    isSaving,
    save,
  } = useInsightInteractions({
    insight,
    source: "longform",
  });

  const {
    account,
    byline,
    tagline,
    entity,
    contentVertical,
    shareableLinks: links,
    totalDiscussions,
    restrictionReason,
    likesCount,
    isPrimer,
    author,
  } = insight;

  const shareBtnOptions = useMemo(() => {
    if (!insight) return {};

    return {
      email: {
        link: `mailto:?subject=\u272a Smartkarma | ${tagline}&body=${tagline}\n${entity?.prettyName} in ${contentVertical?.name}\n\n${byline}\n\nRead more: ${links?.email}`,
      },
      clipboard: {
        message: `${tagline}. Read more: ${links?.direct}`,
      },
      privateMessage: {
        message: `${tagline}. Read more: <a href="${links?.direct}">${links?.direct}</a>`,
      },
      whatsapp: {
        link: `https://web.whatsapp.com/send?text=${tagline}.\nRead more: ${links?.whatsapp}`,
      },
      twitter: {
        link: `https://twitter.com/share?url=${links?.twitter}&text=${tagline}&via=smartkarma`,
      },
      linkedIn: {
        link: `https://www.linkedin.com/shareArticle?mini=true&url=${links?.linkedin}&source=Smartkarma`,
      },
    };
  }, [
    byline,
    contentVertical?.name,
    entity?.prettyName,
    insight,
    links?.direct,
    links?.email,
    links?.linkedin,
    links?.twitter,
    links?.whatsapp,
    tagline,
  ]);

  const [showMenu, toggleMenu] = useToggle(false);

  useEffect(() => {
    if (!isMobile) return;
    if (authenticated) {
      setRightActionButtons(
        <>
          {entity && (
            <>
              <Button
                className="px-0 py-0"
                onClick={() =>
                  authenticated &&
                  router.push(`${router.asPath}/entity-data/${entity.slug}`)
                }
              >
                <Image
                  src={require("./assets/chart-bar.svg")}
                  alt="chart-bar"
                  width={20}
                  height={20}
                />
              </Button>
            </>
          )}
          <ShareButton entity={entity} options={shareBtnOptions} />
          <Button
            className="px-[0.5em]"
            icon={isSaved ? faBookmarkSolid : faBookmark}
            onClick={save}
            isProcessing={isSaving}
          />
          <Button
            className="px-[0.5em]"
            icon={faEllipsisV}
            onClick={toggleMenu}
          />
        </>
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    authenticated,
    entity,
    isMobile,
    isSaved,
    isSaving,
    router,
    shareBtnOptions,
    showEntityData,
  ]);

  if (isMobile) {
    return (
      <div className="fixed z-[999] right-[15px] bottom-[5px]">
        {canLike ? (
          <CircleButton
            className={twMerge(
              "border-[2px] border-solid border-bullish-green",
              isLiked ? "text-bullish-green" : ""
            )}
            icon={isLiked ? faThumbsUpSolid : faThumbsUp}
            disabled={!authenticated}
            onClick={like}
          />
        ) : (
          showLike && (
            <CircleButton
              className={twMerge(
                "border-[2px] border-solid border-bullish-green"
              )}
              icon={faThumbsUp}
            >
              {!!likesCount ? <div>{likesCount}</div> : <></>}
            </CircleButton>
          )
        )}
        {!restrictionReason && (
          <CircleButton
            icon={faCommentDots}
            disabled={!authenticated}
            onClick={() => {
              const element = document.getElementById("insight-discussions");
              element?.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
                inline: "center",
              });
              setTimeout(() => {
                element?.scrollIntoView({
                  behavior: "smooth",
                  block: "nearest",
                  inline: "center",
                });
              }, 500);
            }}
            className={twMerge(
              "flex",
              !!totalDiscussions && "space-x-[2px]",
              "border-[2px] border-solid border-glacial-blue"
            )}
          >
            {!!totalDiscussions && <div>{totalDiscussions}</div>}
          </CircleButton>
        )}
        {!authenticated && (
          <CircleButton
            className={twMerge(
              "border-[2px] border-solid border-bullish-green"
            )}
          >
            <div className="ml-[-2px]">
              <ShareButton
                iconSize="15px"
                noTooltip
                entity={entity}
                options={shareBtnOptions}
                columnView
              />
            </div>
          </CircleButton>
        )}
        {showMenu && (canDislike || account?.id === loggedInAccount?.id) && (
          <div className="fixed left-0 right-0 bottom-0 top-0">
            <div
              className="bg-black/60 fixed left-0 right-0 bottom-0 top-0"
              onClick={toggleMenu}
            ></div>
            <div
              className={twMerge(
                "bg-white pt-[5px] rounded-t-[8px]",
                "fixed bottom-0 left-0 right-0"
              )}
            >
              {canDislike && (
                <button
                  onClick={dislike}
                  className="bg-white outline-none w-full text-left p-[10px] text-active-black border-none"
                >
                  <FontAwesomeIcon
                    icon={isDisliked ? faThumbsDownSolid : faThumbsDown}
                    className={twMerge(isDisliked ? "text-bearish-red" : "")}
                  />{" "}
                  {isDisliked ? "Undo Dislike" : "Dislike / Disagree"}
                </button>
              )}
              {isPrimer && ["76928", 76928].includes(author?.id) && (
                <PrimerDownload
                  insight={insight}
                  authenticated={authenticated}
                  isMobile={isMobile}
                  isPrimer={isPrimer}
                  trackActivity={trackActivity}
                />
              )}
              <RepublishButton
                insight={insight}
                onUpdate={() => mutate?.()}
                className="bg-white outline-none w-full text-left p-[10px] text-active-black border-none"
              />
              <RemovePaywallButton
                insight={insight}
                onUpdate={() => mutate?.()}
                className="bg-white outline-none w-full text-left p-[10px] text-active-black  border-none"
              />
            </div>
          </div>
        )}
        <EntityDataModal
          slug={entity?.slug}
          isOpen={showEntityData}
          onClose={toggleEntityData}
        />
      </div>
    );
  }

  return (
    <>
      {entity && (
        <>
          <CircleButton
            tooltip="Analytics"
            disabled={!authenticated}
            onClick={() => authenticated && toggleEntityData()}
          >
            <Image
              src={require("./assets/chart-bar-dark.svg")}
              alt="chart-bar"
              width={18}
              height={18}
            />
          </CircleButton>
          <EntityDataModal
            slug={entity?.slug}
            isOpen={showEntityData}
            onClose={toggleEntityData}
          />
        </>
      )}
      {!restrictionReason && (
        <CircleButton
          icon={faCommentDots}
          tooltip="Discussion"
          disabled={!authenticated}
          onClick={() => {
            const element = document.getElementById("insight-discussions");
            element?.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
              inline: "center",
            });
            setTimeout(() => {
              element?.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
                inline: "center",
              });
            }, 500);
          }}
          className={twMerge(!!totalDiscussions && "space-x-[2px]")}
        >
          {!!totalDiscussions && <div>{totalDiscussions}</div>}
        </CircleButton>
      )}
      <ShareButton entity={entity} options={shareBtnOptions} />
      <CircleButton
        icon={isSaved ? faBookmarkSolid : faBookmark}
        tooltip={isSaved ? "Unsave" : "Save"}
        disabled={!authenticated}
        isProcessing={isSaving}
        onClick={save}
      />
      {canLike ? (
        <CircleButton
          className={twMerge(
            isLiked
              ? "text-bullish-green"
              : "border-bullish-green border-[3px] border-solid",
            isLiked ? "" : styles.pulse
          )}
          icon={isLiked ? faThumbsUpSolid : faThumbsUp}
          tooltip={isLiked ? "Liked" : "Like"}
          disabled={!authenticated}
          onClick={like}
        />
      ) : (
        showLike && (
          <CircleButton
            className={twMerge("border-bullish-green")}
            icon={faThumbsUp}
            tooltip={
              likesCount
                ? `${likesCount} like${likesCount > 1 ? "s" : ""} received!`
                : "No likes received yet."
            }
          >
            {!!likesCount ? <div>{likesCount}</div> : <></>}
          </CircleButton>
        )
      )}
      {canDislike && (
        <CircleButton
          className={twMerge(isDisliked && "text-bearish-red")}
          icon={isDisliked ? faThumbsDownSolid : faThumbsDown}
          tooltip={isDisliked ? "" : "Dislike / Disagree"}
          disabled={!authenticated}
          onClick={dislike}
        />
      )}
    </>
  );
};

export default InsightCTAs;
